import React, { useState, useEffect, useMemo } from "react";
import api from "../api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function Reports({ user }) {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [practice, setPractice] = useState("");
  const [task, setTask] = useState("");

  const [practices, setPractices] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [visibleCount, setVisibleCount] = useState(20);

  const DAILY_TARGET = 40;

  useEffect(() => {
    loadData();
    loadMasters();
  }, [user]);

  /* LOAD DATA */

  const loadData = async () => {
    try {
      setLoading(true);

      const res = await api.get("/production");

      const userData = (res.data || []).filter(
        (r) => r.user === user.name
      );

      setData(userData);
      setFiltered(userData);
      setVisibleCount(20);
    } catch (err) {
      console.error("Reports load failed:", err);
      setData([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  };

  /* LOAD DROPDOWNS */

  const loadMasters = async () => {
    try {
      const p = await api.get("/practices");
      const t = await api.get("/tasks");

      setPractices(p.data || []);
      setTasks(t.data || []);
    } catch (err) {
      console.error("Reports master load failed:", err);
      setPractices([]);
      setTasks([]);
    }
  };

  /* APPLY FILTERS */

  const applyFilters = () => {
    let temp = [...data];

    if (fromDate) {
      const start = new Date(fromDate);
      start.setHours(0, 0, 0, 0);

      temp = temp.filter(
        (r) => new Date(r.date) >= start
      );
    }

    if (toDate) {
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);

      temp = temp.filter(
        (r) => new Date(r.date) <= end
      );
    }

    if (practice) {
      temp = temp.filter(
        (r) => r.practice === practice
      );
    }

    if (task) {
      temp = temp.filter(
        (r) => r.task === task
      );
    }

    setFiltered(temp);
    setVisibleCount(20);
  };

  const resetFilters = () => {
    setFromDate("");
    setToDate("");
    setPractice("");
    setTask("");

    setFiltered(data);
    setVisibleCount(20);
  };

  /* DOWNLOAD EXCEL */

  const downloadCSV = () => {
    const exportData = filtered.map((row) => {
      const target = Number(
        row.target || DAILY_TARGET
      );

      const productionPercentage = target
        ? (
            (Number(row.production_count || 0) /
              target) *
            100
          ).toFixed(1) + "%"
        : "0.0%";

      return {
        Date: new Date(
          row.date
        ).toLocaleDateString("en-GB"),

        User: row.user,

        Practice: row.practice,

        Task: row.task,

        Achieved: row.production_count,

        Target: target,

        "Production %": productionPercentage
      };
    });

    const worksheet =
      XLSX.utils.json_to_sheet(exportData);

    worksheet["!cols"] = [
      { wch: 15 },
      { wch: 25 },
      { wch: 22 },
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 18 }
    ];

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Production Report"
    );

    const excelBuffer = XLSX.write(
      workbook,
      {
        bookType: "xlsx",
        type: "array"
      }
    );

    const fileData = new Blob(
      [excelBuffer],
      {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      }
    );

    saveAs(
      fileData,
      `Production_Report_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`
    );
  };

  /* SUMMARY */

  const totalAchieved = useMemo(
    () =>
      filtered.reduce(
        (sum, r) =>
          sum +
          Number(r.production_count || 0),
        0
      ),
    [filtered]
  );

  const uniqueDatesCount = useMemo(() => {
    const set = new Set();

    filtered.forEach((r) => {
      if (r.date) {
        set.add(
          new Date(r.date)
            .toISOString()
            .split("T")[0]
        );
      }
    });

    return set.size;
  }, [filtered]);

  const totalTarget =
    uniqueDatesCount * DAILY_TARGET;

  const avgPercent = totalTarget
    ? (
        (totalAchieved / totalTarget) *
        100
      ).toFixed(1)
    : 0;

  const visibleRows = filtered.slice(
    0,
    visibleCount
  );

  const hasMoreRows =
    visibleCount < filtered.length;

  return (
    <div style={page}>
      <div style={header}>
        <div>
          <h2 style={title}>Reports</h2>

          <p style={subtitle}>
            Analyze your productivity with
            filters & insights
          </p>
        </div>

        <button
          onClick={downloadCSV}
          style={downloadBtn}
        >
          ⬇ Download Excel Report
        </button>
      </div>

      <div style={filterCard}>
        <input
          type="date"
          value={fromDate}
          onChange={(e) =>
            setFromDate(e.target.value)
          }
          style={input}
        />

        <input
          type="date"
          value={toDate}
          onChange={(e) =>
            setToDate(e.target.value)
          }
          style={input}
        />

        <select
          value={practice}
          onChange={(e) =>
            setPractice(e.target.value)
          }
          style={input}
        >
          <option value="">
            All Practices
          </option>

          {practices.map((p) => (
            <option
              key={p.id}
              value={p.name}
            >
              {p.name}
            </option>
          ))}
        </select>

        <select
          value={task}
          onChange={(e) =>
            setTask(e.target.value)
          }
          style={input}
        >
          <option value="">
            All Tasks
          </option>

          {tasks.map((t) => (
            <option
              key={t.id}
              value={t.name}
            >
              {t.name}
            </option>
          ))}
        </select>

        <button
          onClick={applyFilters}
          style={btn}
        >
          Apply Filters
        </button>

        <button
          onClick={resetFilters}
          style={resetBtn}
        >
          Reset
        </button>
      </div>

      <div style={summaryGrid}>
        <div style={card}>
          <p style={cardLabel}>
            Total Achieved
          </p>

          <h3 style={cardValue}>
            {totalAchieved}
          </h3>
        </div>

        <div style={card}>
          <p style={cardLabel}>
            Total Target
          </p>

          <h3 style={cardValue}>
            {totalTarget}
          </h3>
        </div>

        <div style={card}>
          <p style={cardLabel}>
            Avg Productivity
          </p>

          <h3 style={cardValue}>
            {avgPercent}%
          </h3>
        </div>

        <div style={card}>
          <p style={cardLabel}>
            Entries
          </p>

          <h3 style={cardValue}>
            {filtered.length}
          </h3>
        </div>
      </div>

      <div style={tableCard}>
        <table style={table}>
          <thead>
            <tr style={thead}>
              <th style={th}>Date</th>
              <th style={th}>User</th>
              <th style={th}>Practice</th>
              <th style={th}>Task</th>
              <th style={th}>Achieved</th>
              <th style={th}>Target</th>
              <th style={th}>%</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan="7"
                  style={{
                    ...td,
                    textAlign: "center",
                    color: "#64748B"
                  }}
                >
                  Loading...
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td
                  colSpan="7"
                  style={{
                    ...td,
                    textAlign: "center",
                    color: "#64748B"
                  }}
                >
                  No records found
                </td>
              </tr>
            ) : (
              visibleRows.map((r) => {
                const target = Number(
                  r.target || DAILY_TARGET
                );

                const percent = target
                  ? (
                      (Number(
                        r.production_count || 0
                      ) /
                        target) *
                      100
                    ).toFixed(1)
                  : "0.0";

                return (
                  <tr
                    key={r.id}
                    style={row}
                  >
                    <td style={td}>
                      {new Date(
                        r.date
                      ).toLocaleDateString()}
                    </td>

                    <td style={td}>
                      {r.user}
                    </td>

                    <td style={td}>
                      {r.practice}
                    </td>

                    <td style={td}>
                      {r.task}
                    </td>

                    <td style={td}>
                      {r.production_count}
                    </td>

                    <td style={td}>
                      {target}
                    </td>

                    <td
                      style={{
                        ...td,
                        color: "#0F766E",
                        fontWeight: "600"
                      }}
                    >
                      {percent}%
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {!loading && hasMoreRows && (
          <div style={loadMoreWrap}>
            <button
              onClick={() =>
                setVisibleCount(
                  (prev) => prev + 20
                )
              }
              style={loadMoreBtn}
            >
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* STYLES */

const page = {
  padding: "30px",
  fontFamily: "sans-serif",
  background: "#F8FAFC"
};

const header = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "20px",
  gap: "12px",
  flexWrap: "wrap"
};

const title = {
  margin: 0,
  fontSize: "26px",
  fontWeight: "700",
  color: "#0F172A"
};

const subtitle = {
  marginTop: "5px",
  color: "#64748B",
  fontSize: "14px"
};

const filterCard = {
  display: "flex",
  gap: "15px",
  flexWrap: "wrap",
  background: "#fff",
  padding: "18px",
  borderRadius: "14px",
  boxShadow:
    "0 6px 20px rgba(0,0,0,0.05)",
  marginBottom: "20px"
};

const input = {
  padding: "10px",
  border: "1px solid #CCFBF1",
  borderRadius: "8px",
  outline: "none"
};

const btn = {
  background: "#0F766E",
  color: "#fff",
  border: "none",
  padding: "10px 18px",
  borderRadius: "8px",
  fontWeight: "600",
  cursor: "pointer"
};

const resetBtn = {
  background: "#E2E8F0",
  color: "#0F172A",
  border: "none",
  padding: "10px 18px",
  borderRadius: "8px",
  fontWeight: "600",
  cursor: "pointer"
};

const downloadBtn = {
  background: "#14B8A6",
  color: "#fff",
  border: "none",
  padding: "10px 18px",
  borderRadius: "10px",
  fontWeight: "600",
  cursor: "pointer"
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns:
    "repeat(4,1fr)",
  gap: "20px",
  marginBottom: "25px"
};

const card = {
  background: "#fff",
  padding: "20px",
  borderRadius: "16px",
  boxShadow:
    "0 8px 24px rgba(0,0,0,0.06)"
};

const cardLabel = {
  margin: 0,
  color: "#64748B",
  fontSize: "13px"
};

const cardValue = {
  margin: "10px 0 0",
  fontSize: "26px",
  fontWeight: "700",
  color: "#0F172A"
};

const tableCard = {
  background: "#fff",
  padding: "20px",
  borderRadius: "16px",
  boxShadow:
    "0 8px 24px rgba(0,0,0,0.06)"
};

const table = {
  width: "100%",
  borderCollapse: "collapse"
};

const thead = {
  background: "#ECFEFF"
};

const th = {
  padding: "12px",
  textAlign: "left",
  borderBottom: "1px solid #CCFBF1",
  fontSize: "13px",
  color: "#0F172A"
};

const td = {
  padding: "12px",
  borderBottom: "1px solid #F1F5F9",
  fontSize: "13px"
};

const row = {
  transition: "0.2s"
};

const loadMoreWrap = {
  display: "flex",
  justifyContent: "center",
  marginTop: "18px"
};

const loadMoreBtn = {
  background: "#ECFEFF",
  color: "#0F766E",
  border: "1px solid #CCFBF1",
  padding: "10px 18px",
  borderRadius: "10px",
  fontWeight: "700",
  cursor: "pointer"
};

export default Reports;
